import React from 'react';
import { useAtomValue } from 'jotai';
import { dcbAtom, languageAtom } from '@shared/store/slice-atoms';
import { removeZerosfromPrice } from '@shared/utils/numbers';
import { useTranslation } from 'react-i18next';
import {
  isOrangeMoroccoApiSource,
  isSpecialApiSourceForOperatorThirdText,
} from '@shared/utils/general';
import { PAYMENT_PLANS } from '@entities/payment-plan';
import { isMA } from '@entities/country';

const PLACEHOLDER_OPERATOR_NAME = 'Select your operator';

type PinFormOperatorTextProps = {
  isBHZainSLAOperator?: boolean;
};

export const PinFormOperatorText = ({
  isBHZainSLAOperator = false,
}: PinFormOperatorTextProps) => {
  const { t } = useTranslation();

  const language = useAtomValue(languageAtom);
  const dcb = useAtomValue(dcbAtom);
  const homeData = dcb.homeData;
  const subscribeData = dcb.subscribeData;
  const operator = dcb.operator;
  const fallbackOperator = dcb.fallbackOperator;
  const operatorSelectedDetails = dcb.operatorSelectedDetails;

  const isAR = language.locale === 'ar';
  const isOrangeMoroccoSource = isOrangeMoroccoApiSource(homeData?.source);
  const getConfirmPinText = (key: string) =>
    isOrangeMoroccoSource ? t(key, { lng: 'fr' }) : t(key);

  const confirmPinTextAmountAndFrequency = subscribeData?.priceLocalCurrency
    ? `${subscribeData.priceLocalCurrency}`
    : `${removeZerosfromPrice(
        String(
          subscribeData.selectedPrice ??
            subscribeData.price ??
            homeData.offerMobileText ??
            ''
        )
      )}${
        isAR && subscribeData?.currency_ar ? subscribeData?.currency_ar : subscribeData.currency
      }${' '} ${
        isAR && subscribeData?.frequency_ar ? subscribeData?.frequency_ar : subscribeData.frequency
      }`;

  const selectedOperatorName =
    operator?.name && operator.name !== PLACEHOLDER_OPERATOR_NAME ? operator.name : undefined;

  const operatorCode = operator?.value || subscribeData?.operatorName;
  const operatorFromList = homeData?.operators?.find(
    (item) => (item as { value?: string }).value === operatorCode
  );

  const readableOperatorName =
    operatorSelectedDetails?.operatorDisplayName ||
    operatorSelectedDetails?.name ||
    selectedOperatorName ||
    operatorFromList?.name ||
    fallbackOperator ||
    subscribeData?.operatorName;

  const operatorDisplayName = isBHZainSLAOperator
    ? operatorSelectedDetails?.operatorDisplayName || readableOperatorName
    : readableOperatorName;

  const isOrangeMoroccoEnglishPage =
    isMA(homeData?.country) && (isOrangeMoroccoSource || isOrangeMoroccoApiSource(dcb.source));

  const sentenceTail = isOrangeMoroccoEnglishPage
    ? t('confirm_pin_automatic_renewal')
    : operatorDisplayName;

  if (isSpecialApiSourceForOperatorThirdText(homeData?.source)) {
    const dailyThirdText = operatorSelectedDetails?.dailyThirdText;
    const weeklyThirdText = operatorSelectedDetails?.weeklyThirdText;
    const monthlyThirdText = operatorSelectedDetails?.monthlyThirdText;

    if (subscribeData?.billingOption === PAYMENT_PLANS.daily && dailyThirdText) {
      return <p className="mt-3 fs-10">{dailyThirdText}</p>;
    }

    if (subscribeData?.billingOption === PAYMENT_PLANS.weekly && weeklyThirdText) {
      return <p className="mt-3 fs-10">{weeklyThirdText}</p>;
    }

    if (subscribeData?.billingOption === PAYMENT_PLANS.monthly && monthlyThirdText) {
      return <p className="mt-3 fs-10">{monthlyThirdText}</p>;
    }

    if (dailyThirdText) return <p className="mt-3 fs-10">{dailyThirdText}</p>;

    if (weeklyThirdText) return <p className="mt-3 fs-10">{weeklyThirdText}</p>;

    if (monthlyThirdText) return <p className="mt-3 fs-10">{monthlyThirdText}</p>;
  }

  if (subscribeData?.removeConfirmPinText) {
    return <p className="mt-3 fs-10">{getConfirmPinText('confirm_pin_text_3')}</p>;
  }

  return (
    <p className="mt-3 fs-10">
      {getConfirmPinText('confirm_pin_text_1')} <b>{confirmPinTextAmountAndFrequency}</b>{' '}
      {getConfirmPinText('confirm_pin_text_2')} {sentenceTail} <br />{' '}
      {getConfirmPinText('confirm_pin_text_3')}
    </p>
  );
};
